/**
 * HabitPraise Component - Celebrates positive spending habit changes
 * Features:
 * - Detects reduced spending in tracked categories
 * - Praises users at 7, 14, and 30 day milestones
 * - Dismissible celebration cards
 */

import { useState, useEffect } from 'react';
import { apiGet, apiPost } from '@/utils/api';
import confetti from 'canvas-confetti';

// Category icons and labels
const CATEGORY_CONFIG = {
  dining: { icon: '🍔', label: 'Dining Out', color: 'orange' },
  coffee: { icon: '☕', label: 'Coffee', color: 'amber' },
  subscriptions: { icon: '📺', label: 'Subscriptions', color: 'purple' },
  shopping: { icon: '🛒', label: 'Shopping', color: 'pink' },
  entertainment: { icon: '🎮', label: 'Entertainment', color: 'blue' },
  other: { icon: '💸', label: 'Spending', color: 'gray' }
};

// Milestone messages
const getMilestoneMessage = (days, category, reduction, saved) => {
  const cat = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.other;
  
  if (days === 7) {
    return {
      headline: `1 Week Streak! 🔥`,
      message: `You've cut ${cat.label.toLowerCase()} spending by ${reduction}% this week!`,
      submessage: `That's $${saved.toFixed(0)} saved so far. Keep it up!`
    };
  }
  if (days === 14) {
    return {
      headline: `2 Week Champion! 🏆`,
      message: `Your ${cat.label.toLowerCase()} habit is officially breaking!`,
      submessage: `$${saved.toFixed(0)} saved in 2 weeks. You're building real change!`
    };
  }
  if (days === 30) {
    return {
      headline: `Monthly Master! 🎖️`,
      message: `30 days of crushing your ${cat.label.toLowerCase()} habit!`,
      submessage: `$${saved.toFixed(0)} saved this month. That's a lifestyle change!`
    };
  }
  return { headline: 'Nice!', message: 'Keep up the good work!', submessage: '' };
};

// Single Praise Card
function PraiseCard({ improvement, onDismiss }) {
  const cat = CATEGORY_CONFIG[improvement.category] || CATEGORY_CONFIG.other;
  const milestone = getMilestoneMessage(
    improvement.days,
    improvement.category,
    improvement.reduction_percent,
    improvement.saved_amount
  );

  const handleDismiss = async () => {
    // Trigger confetti before dismissing
    confetti({
      particleCount: 60,
      spread: 50,
      origin: { y: 0.7 },
      colors: ['#10B981', '#F59E0B', '#8B5CF6']
    });
    
    // Acknowledge the milestone
    await apiPost(`/habits/acknowledge/${improvement.category}/${improvement.milestone}`);
    onDismiss(improvement);
  };

  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-${cat.color}-500 to-${cat.color}-600 p-1`}>
      <div className="bg-white rounded-xl p-5">
        {/* Icon Badge */}
        <div className="absolute -top-2 -right-2 w-16 h-16 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg transform rotate-12">
          <span className="text-3xl transform -rotate-12">{cat.icon}</span>
        </div>

        {/* Content */}
        <div className="pr-14">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
            {improvement.days} Day Milestone
          </p>
          <h3 className="text-xl font-black text-gray-900 mb-1">
            {milestone.headline}
          </h3>
          <p className="text-gray-700 font-medium mb-1">
            {milestone.message}
          </p>
          <p className="text-sm text-gray-500">
            {milestone.submessage}
          </p>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100">
          <div className="flex-1">
            <p className="text-xs text-gray-500">Was spending</p>
            <p className="text-lg font-bold text-red-500 line-through">
              ${improvement.historical_daily}/day
            </p>
          </div>
          <div className="text-3xl">→</div>
          <div className="flex-1">
            <p className="text-xs text-gray-500">Now spending</p>
            <p className="text-lg font-bold text-green-600">
              ${improvement.current_daily}/day
            </p>
          </div>
        </div>

        {/* Celebrate Button */}
        <button
          onClick={handleDismiss}
          className={`mt-4 w-full py-3 bg-gradient-to-r from-${cat.color}-500 to-${cat.color}-600 text-white font-bold rounded-xl hover:from-${cat.color}-600 hover:to-${cat.color}-700 transition-all shadow-lg`}
          data-testid={`celebrate-${improvement.category}-${improvement.days}`}
        >
          🎉 Celebrate & Continue
        </button>
      </div>
    </div>
  );
}

// Main Component
export default function HabitPraise({ className = '' }) {
  const [improvements, setImprovements] = useState([]);
  const [totalSaved, setTotalSaved] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHabits();
  }, []);

  const loadHabits = async () => {
    setLoading(true);
    try {
      const response = await apiGet('/habits/improvements');
      if (response.ok) {
        const data = await response.json();
        setImprovements(data.improvements || []);
        setTotalSaved(data.total_saved || 0);
      }
    } catch (error) {
      console.error('Error loading habits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = (dismissed) => {
    setImprovements(prev => prev.filter(i => 
      !(i.category === dismissed.category && i.milestone === dismissed.milestone)
    ));
  };

  if (loading) {
    return null; // Don't show loading state for this component
  }

  if (improvements.length === 0) {
    return null; // Don't show anything if no improvements
  }

  return (
    <div className={className}>
      {/* Header with total saved */}
      {totalSaved > 50 && (
        <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
          <div className="flex items-center gap-3">
            <span className="text-3xl">💰</span>
            <div>
              <p className="text-sm font-medium text-green-800">
                Your habits are paying off!
              </p>
              <p className="text-2xl font-black text-green-600">
                ${totalSaved.toFixed(0)} saved by changing habits
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Praise Cards */}
      <div className="space-y-4">
        {improvements.map((improvement, index) => (
          <PraiseCard
            key={`${improvement.category}-${improvement.milestone}`}
            improvement={improvement}
            onDismiss={handleDismiss}
          />
        ))}
      </div>
    </div>
  );
}

// Export for use in other components
export { PraiseCard, getMilestoneMessage };
